import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ClientRepresentation, KeycloakAdminApi } from '../../services/keycloak-admin-api';
import { RouterLink } from '@angular/router';
import { UserRoles } from '../../auth/user-roles';
import { environment } from '../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-clients-list',
  imports: [RouterLink, ReactiveFormsModule],
  templateUrl: './clients-list.html',
  styleUrl: './clients-list.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ClientsList implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private userRoles = inject(UserRoles);

  clients: ClientRepresentation[] = [];
  canCreateNewClient = signal(false);

  // Pagination & search
  currentPage = 1;
  pageSize = 10;
  totalClients = 0;
  pageSizeOptions = [10, 20, 50, 100];
  searchControl = new FormControl('');
  searchTerm = '';
  isLoading = false;
  errorMessage: string | null = null;

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((value) => {
        this.searchTerm = value || '';
        this.currentPage = 1;
        console.log('Search term changed to:', this.searchTerm);
        this.loadClients(this.searchTerm || undefined);
      });
  }

  ngOnInit() {
    this.loadClients();
    this.canCreateNewClient.set(this.userRoles.hasRole(environment.realmResource, 'create-client'));
  }

  async loadClients(search?: string) {
    this.isLoading = true;
    this.errorMessage = null;
    const first = (this.currentPage - 1) * this.pageSize;
    try {
      const clients = await this.keycloakApi.getClientsPaginated(search, first, this.pageSize);
      this.clients = clients;
      // Estimate total: if page is full, assume more pages exist
      if (clients.length === this.pageSize) {
        this.totalClients = this.currentPage * this.pageSize + 1; // Indicate more exist
      } else {
        this.totalClients = (this.currentPage - 1) * this.pageSize + clients.length;
      }
      this.isLoading = false;
    } catch (error) {
      this.errorMessage = `Failed to load clients: ${error}`;
      this.isLoading = false;
      setTimeout(() => (this.errorMessage = null), 5000);
    }
  }

  refreshClients() {
    this.loadClients(this.searchTerm || undefined);
  }

  onPageChange(event: Event) {
    const customEvent = event as CustomEvent;
    this.currentPage = customEvent?.detail?.currentPage ?? 1;
    this.loadClients(this.searchTerm || undefined);
  }

  onPageSizeChange(event: Event) {
    const customEvent = event as CustomEvent;
    this.pageSize = customEvent?.detail?.pageSize ?? 10;
    this.currentPage = 1;
    this.loadClients(this.searchTerm || undefined);
  }

  // No longer needed: onSearchChange

  totalPages(): number {
    return Math.ceil(this.totalClients / this.pageSize);
  }

  startIndex(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  endIndex(): number {
    const calculated = this.currentPage * this.pageSize;
    return Math.min(calculated, this.totalClients);
  }

  clearSearch() {
    this.searchControl.setValue('');
  }
}
